import React, { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import ContentCard from './ContentCard/ContentCard';
import MiniRichTextEditor from '../../../../../components/forms/Input/MiniRichTextEditor';
import FormToolbar from './FormToolbar';
import {
  patchForm,
  postReorderContents,
} from '../../../../../services/forms/formsService';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import StrictModeDroppable from '../../../../../components/dnd/StrictModeDroppable';
import { drugReorder } from '../../../../../utils/helpers/helpers';
import { useFormEdit } from '../FormEditContext';

// Состояние формы (useForm) поднято в EditForm: ему нужен form.can_edit, а
// второй вызов useForm здесь дублировал бы запрос формы.
export default function Questions({
  form,
  contents,
  setContents,
  fetchContents,
  points,
  setPoints,
}) {
  const { uuid } = useParams();
  const { readOnly, reportError } = useFormEdit();

  const [selected, setSelected] = useState(uuid);
  const [title, setTitle] = useState('');
  const [draggingId, setDraggingId] = useState();

  const titleTimerRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionTimerRef = useRef(null);

  const onDragEnd = (result) => {
    setDraggingId(null);
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }

    const reorderedItems = drugReorder(
      contents,
      result.source.index,
      result.destination.index,
    );
    setContents(reorderedItems);

    // Сохраняем порядок целиком, а не order одного вопроса: в старых формах
    // order повторяется и с пропусками, и правка одного значения записала бы
    // не тот порядок, что на экране.
    postReorderContents(
      uuid,
      reorderedItems.map((content) => content.uuid),
    )?.catch((err) => {
      reportError(err, 'Не удалось сохранить порядок вопросов');
      // вернуть порядок, который на самом деле сохранён
      fetchContents()?.catch(() => {});
    });
  };

  const saveForm = useCallback(
    (value, field) => {
      patchForm(uuid, { [field]: value })
        ?.then((res) => {
          setTitle(res.name);
        })
        .catch((err) => reportError(err));
    },
    [reportError, uuid],
  );

  const debounceSaveData = useCallback(
    (value, ref, field) => {
      if (ref.current) {
        clearTimeout(ref.current);
      }
      ref.current = setTimeout(() => {
        if (field === 'name' && value === '') {
          titleRef.current.innerHTML = title || form.name;
          return;
        }

        saveForm(value, field);
      }, 1000);
    },
    [saveForm, title, form?.name],
  );

  // Вызывается до того, как библиотека замерит карточки: сворачиваем их
  // (ContentCard по draggingId) синхронно — React 18 иначе отложил бы рендер,
  // и замер достался бы развёрнутым карточкам.
  const onBeforeCapture = (before) => {
    document.activeElement?.blur?.();
    flushSync(() => setDraggingId(before.draggableId));
  };

  return (
    <div className='relative flex min-w-[48rem] max-w-3xl flex-col gap-4'>
      {!readOnly && selected === uuid && (
        <FormToolbar update={fetchContents} uuid={uuid} />
      )}
      <div
        onClick={() => setSelected(uuid)}
        className={`flex flex-col gap-2 rounded-lg border-l-[0.4rem]
                  bg-white p-6 ${
                    selected === uuid ? 'border-l-blue-500' : 'border-l-white'
                  } border-t-[0.6rem] border-t-violet-700`}
      >
        <MiniRichTextEditor
          ref={titleRef}
          borderColor='text-violet-700'
          className={`${
            selected === uuid && 'border-b'
          } min-h-[3.125rem] text-3xl focus:outline-none`}
          contentEditable={!readOnly}
          defaultValue={form?.name}
          onChange={(e) => {
            debounceSaveData(e, titleTimerRef, 'name');
          }}
        />
        <MiniRichTextEditor
          borderColor='text-violet-700'
          className={`${selected === uuid && 'border-b'} focus:outline-none`}
          list
          contentEditable={!readOnly}
          defaultValue={form?.descritption} // fix description
          onChange={(e) => {
            debounceSaveData(e, descriptionTimerRef, 'descritption'); // fix description
          }}
          placeholder={'Form description'} // to translate
        />
        <div className='flex flex-row justify-end'>total points: {points} </div>
      </div>
      <DragDropContext onBeforeCapture={onBeforeCapture} onDragEnd={onDragEnd}>
        <StrictModeDroppable droppableId='contents'>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className='flex flex-col gap-4'
            >
              {contents?.map((content, i) => {
                let isSelected = selected === content?.uuid;
                return (
                  <Draggable
                    key={content.uuid}
                    // uuid, а не order: у разных вопросов order может совпадать,
                    // и тогда библиотека путает карточки при перетаскивании
                    draggableId={content.uuid}
                    index={i}
                    isDragDisabled={readOnly}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        // {...provided.dragHandleProps}
                        style={{
                          userSelect: 'none',
                          // backgroundColor: '#fff',
                          // border: '1px solid lightgrey',
                          ...provided.draggableProps.style,
                        }}
                        className='relative'
                        key={content?.uuid}
                      >
                        {isSelected && !readOnly && !draggingId && (
                          <FormToolbar
                            update={fetchContents}
                            uuid={uuid}
                            // сразу после выбранного вопроса
                            order={i + 2}
                          />
                        )}
                        <ContentCard
                          id={content?.uuid}
                          content={content}
                          contents={contents}
                          selected={isSelected}
                          selectContent={() => setSelected(content?.uuid)}
                          update={() => fetchContents()}
                          setContents={setContents}
                          setPoints={setPoints}
                          dragProps={provided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                          draggingId={draggingId}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </StrictModeDroppable>
      </DragDropContext>
    </div>
  );
}
